import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import { count, followUpsOn, formatMoney, listPartners, listPeople, loadAllRows, loadOverview, personTypes, showTrades } from './adminData.js';
import { countByWeek } from './chartData.js';
import { Bars, WeekColumns } from './Charts.jsx';
import { lastOutreach, loadOutreach } from './outreachData.js';
import { isAutomaticReply } from './outreachTracking.js';
import { formatWhen } from './staffAdmin.js';

const positive = (value) => Number(value) > 0;
const noRows = [];

function Tile({ value, label, alert = false }) {
  return <div className={alert ? 'mod-alert' : undefined}><strong>{value}</strong><span>{label}</span></div>;
}

/**
 * From the conversations the outreach was built from: every email Naima sent, each provider once when a person first wrote back,
 * and each provider whose only answer is an automatic reply that nobody has emailed them after (at that automatic reply).
 */
function outreachTimes(rows) {
  const times = { sent: [], replied: [], automatic: [] };
  for (const row of rows) {
    const chain = row.chain ?? noRows;
    for (const email of chain) if (email.kind === 'sent') times.sent.push(email.at);
    if (row.repliedAt) {
      times.replied.push(row.repliedAt);
      continue;
    }
    const autoReply = chain.filter((email) => email.kind === 'received' && isAutomaticReply(email.subject)).at(-1);
    if (autoReply && !chain.some((email) => email.kind === 'sent' && Date.parse(email.at) > Date.parse(autoReply.at))) times.automatic.push(autoReply.at);
  }
  return times;
}

/**
 * The admin's landing screen, as charts: housing provider outreach by week and how far it has got, new users by week and by
 * type, and jobs by kind and where they are now, with the remaining figures listed under each. `onOpen(params)` switches tab.
 */
export default function OverviewTab({ onOpen }) {
  const [state, setState] = useState({ overview: null, error: '' });
  // Naima's emails take a moment to read from Resend, so they load on their own and never hold up (or break) the rest.
  const [outreach, setOutreach] = useState(() => ({ data: lastOutreach(), error: '' }));
  // When each account joined, and how many housing providers answered Naima: for the charts only, so a failure just leaves them out.
  const [joined, setJoined] = useState(null);
  const [partners, setPartners] = useState(null);
  const latest = useRef(0);
  const open = (params) => () => onOpen?.(params);

  const loadFigures = useCallback(async () => {
    try {
      const overview = await loadOverview(supabase);
      setState({ overview: overview || {}, error: '' });
    } catch (cause) {
      setState((current) => ({ overview: current.overview, error: cause.message || 'Could not load the overview. Please try again.' }));
    }
  }, []);
  const loadProviders = useCallback(async () => {
    const ticket = ++latest.current;
    try {
      const data = await loadOutreach(supabase, { isCurrent: () => ticket === latest.current });
      if (!data || ticket !== latest.current) return;
      setOutreach({ data, error: '' });
    } catch (cause) {
      if (ticket !== latest.current) return;
      setOutreach((current) => ({ data: current.data, error: cause.message || "Could not load the outreach emails. Please try again." }));
    }
  }, []);
  const loadCharts = useCallback(async () => {
    const [people, answered] = await Promise.allSettled([
      loadAllRows((page) => listPeople(supabase, { type: 'all', ...page })),
      listPartners(supabase),
    ]);
    if (people.status === 'fulfilled' && people.value) setJoined(people.value.rows.map((person) => person.joined_at));
    if (answered.status === 'fulfilled') setPartners(answered.value?.counts || {});
  }, []);
  const load = useCallback(() => { void loadFigures(); void loadProviders(); void loadCharts(); }, [loadFigures, loadProviders, loadCharts]);
  useEffect(() => {
    load();
    return () => { latest.current += 1; };
  }, [load]);

  const { overview, error } = state;
  if (!overview && !error) return <p>Loading the overview…</p>;

  const refresh = <button type="button" className="admin-link" onClick={load}>Refresh</button>;
  if (!overview) {
    return (
      <>
        <p className="mod-error" role="alert">{error}</p>
        <p>{refresh}</p>
      </>
    );
  }

  const people = overview.people || {};
  const types = overview.types || {};
  const companies = overview.companies || {};
  const residents = overview.residents || {};
  const jobs = overview.jobs || {};
  const trades = overview.trades || {};
  const summary = outreach.data?.summary;
  const reviews = Number(trades.reviews) || 0;
  const rating = Number(trades.average_rating);
  const hasRating = reviews > 0 && trades.average_rating !== null && trades.average_rating !== undefined && Number.isFinite(rating);
  const emailWeeks = outreach.data ? countByWeek(outreachTimes(outreach.data.rows), { weeks: 8 }) : null;
  const joinWeeks = joined ? countByWeek({ joined }, { weeks: 12 }) : null;

  return (
    <>
      {error && <p className="mod-error" role="alert">{error}</p>}
      <div className="ov-grid">
        <section className="ov-panel" aria-labelledby="ov-users">
          <h2 className="admin-section" id="ov-users">Users</h2>
          {joinWeeks && (
            <>
              <h3 className="admin-subsection">New accounts by week</h3>
              <WeekColumns title="New accounts by week" series={[{ key: 'joined', label: 'New accounts', color: 'var(--viz-1)' }]} weeks={joinWeeks}/>
            </>
          )}
          <h3 className="admin-subsection">By type</h3>
          <Bars
            title="Users by type"
            rows={personTypes.map(([key, label]) => ({
              key, label, value: Number(types[key]?.total) || 0, note: `+${count(types[key]?.new_30d)} in 30 days`, onOpen: open({ tab: 'people', type: key }),
            }))}
          />
          <div className="mod-summary" aria-label="People summary">
            <Tile value={count(people.total)} label="Accounts"/>
            <Tile value={count(people.new_7d)} label="New this week"/>
            <Tile value={count(people.new_30d)} label="New in the last 30 days"/>
            <Tile value={count(people.signed_in_30d)} label="Signed in during the last 30 days"/>
            <Tile value={count(people.confirmed)} label="Email confirmed"/>
          </div>
        </section>

        <section className="ov-panel" aria-labelledby="ov-outreach">
          <h2 className="admin-section" id="ov-outreach">Housing provider outreach</h2>
          {outreach.error && <p className="mod-error" role="alert">{outreach.error}</p>}
          {!summary && !outreach.error && <p className="mod-note">Loading outreach emails…</p>}
          {summary && (
            <>
              <h3 className="admin-subsection">Outreach emails by week</h3>
              <WeekColumns
                title="Outreach emails by week"
                series={[
                  { key: 'sent', label: 'Sent', color: 'var(--viz-1)' },
                  { key: 'replied', label: 'Replied', color: 'var(--viz-2)' },
                  { key: 'automatic', label: 'Automatic reply, not followed up', color: 'var(--viz-3)' },
                ]}
                weeks={emailWeeks}
              />
              <h3 className="admin-subsection">How far it has got</h3>
              <Bars
                title="How far housing provider outreach has got"
                rows={[
                  { key: 'emailed', label: 'Emailed', value: summary.emailed },
                  { key: 'replied', label: 'Replied', value: summary.replied },
                  ...(partners ? [
                    { key: 'accepting', label: 'Accepting referrals', value: Number(partners.accepted) || 0 },
                    { key: 'full', label: 'No spaces right now', value: Number(partners.no_capacity) || 0 },
                  ] : []),
                ]}
              />
              <div className="mod-summary" aria-label="Housing provider outreach summary">
                {followUpsOn && <Tile value={count(summary.dueNow)} label="Follow-ups due" alert={positive(summary.dueNow)}/>}
                <Tile value={count(summary.bounced)} label="Bounced" alert={positive(summary.bounced)}/>
              </div>
            </>
          )}
          <p><button type="button" className="admin-link" onClick={open({ tab: 'outreach' })}>See provider outreach</button></p>
        </section>

        <section className="ov-panel" aria-labelledby="ov-jobs">
          <h2 className="admin-section" id="ov-jobs">Jobs</h2>
          <h3 className="admin-subsection">Posted</h3>
          <Bars
            title="Jobs posted"
            rows={[
              { key: 'home', label: 'Home jobs', value: Number(jobs.home_jobs) || 0 },
              { key: 'commercial', label: 'Commercial jobs', value: Number(jobs.commercial_jobs) || 0 },
              { key: 'tenders', label: 'Private tenders', value: Number(jobs.private_tenders) || 0 },
            ]}
          />
          <h3 className="admin-subsection">Where they are now</h3>
          <Bars
            title="Where jobs are now"
            rows={[
              { key: 'open', label: 'Open now', value: Number(jobs.open) || 0 },
              { key: 'active', label: 'Work in progress', value: Number(jobs.work_active) || 0 },
              { key: 'completed', label: 'Work completed', value: Number(jobs.work_completed) || 0 },
              { key: 'disputed', label: 'Disputed', value: Number(jobs.work_disputed) || 0 },
            ]}
          />
          <div className="mod-summary" aria-label="Jobs summary">
            <Tile value={count(jobs.bids_30d)} label="Bids in the last 30 days"/>
            <Tile value={formatMoney(jobs.completed_value_minor)} label="Value of completed work"/>
          </div>
          <p><button type="button" className="admin-link" onClick={open({ tab: 'jobs' })}>See all jobs</button></p>
        </section>

        <section className="ov-panel" aria-labelledby="ov-residents">
          <h2 className="admin-section" id="ov-residents">Residents</h2>
          <div className="mod-summary" aria-label="Residents summary">
            <Tile value={count(residents.with_home)} label="With a home"/>
            <Tile value={count(residents.homes_linked_to_provider)} label="Homes linked to a housing provider"/>
            <Tile value={count(residents.repairs)} label="Repairs reported"/>
            <Tile value={count(residents.repairs_open)} label="Open repairs"/>
            <Tile value={count(residents.homes_seekers)} label="Looking for a home"/>
            <Tile value={count(residents.applications_30d)} label="Room applications in the last 30 days"/>
            <Tile
              value={count(residents.id_checks_waiting)}
              alert={positive(residents.id_checks_waiting)}
              label={<button type="button" className="admin-link" onClick={open({ tab: 'ids' })}>ID checks waiting</button>}
            />
            <Tile value={count(residents.id_verified)} label="ID verified"/>
          </div>
          <h2 className="admin-section">Other companies</h2>
          <div className="mod-summary" aria-label="Other companies summary">
            <Tile value={count(companies.buyer)} label="Commercial buyers"/>
            <Tile value={count(companies.housing_provider)} label={`Housing providers · ${count(companies.housing_provider_verified)} verified`}/>
          </div>
          {showTrades && (
            <>
              <h2 className="admin-section">Trades</h2>
              <div className="mod-summary" aria-label="Trades summary">
                <Tile value={count(companies.trade)} label="Trade companies"/>
                <Tile value={count(companies.trade_verified)} label="Verified companies"/>
                <Tile value={count(trades.subscribed)} label="Subscribed"/>
                <Tile value={count(trades.lapsed)} label="Lapsed"/>
                <Tile value={count(trades.free)} label="On free bids"/>
                <Tile value={count(trades.bids_this_month)} label="Bids this month"/>
                <Tile
                  value={hasRating ? rating.toFixed(1) : '–'}
                  label={hasRating ? `Average rating · from ${count(reviews)} ${reviews === 1 ? 'review' : 'reviews'}` : 'Average rating · no reviews yet'}
                />
              </div>
              {trades.bid_limit_on === false && <p className="mod-note">Bid limits are switched off, so every trade can bid as often as they like.</p>}
              <p><button type="button" className="admin-link" onClick={open({ tab: 'trades' })}>See all trades</button></p>
            </>
          )}
        </section>
      </div>

      <p className="mod-note">{overview.generated_at ? <>{`Figures as of ${formatWhen(overview.generated_at)} · `}{refresh}</> : refresh}</p>
    </>
  );
}
